import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MotorQuotationDataService } from "src/app/share/data-service/motor/motor-quotation.data.service";
import {
  MotorRequestInterface,
  VehicleInfoSelectionInterface
} from "src/app/share/interface/motor.interface";
import { AlertService } from "src/app/share/service/alert.service";
import { ScreenSizeService } from "src/app/share/service/screen-size.service";

type VehicleField =
  | "make"
  | "model"
  | "year"
  | "variant"
  | "series"
  | "engineCapacity"
  | "transmission";

interface VehicleOption {
  value: string | number;
  label: string;
}

// Cascade order: each field's options are narrowed by every field before it.
// Year comes before variant (PSSR-3370).
const VEHICLE_FIELDS: VehicleField[] = [
  "make",
  "model",
  "year",
  "variant",
  "series",
  "engineCapacity",
  "transmission"
];

// Retrieved values for these fields are locked (PSSR-3257); the rest stay editable.
const LOCKABLE_FIELDS: VehicleField[] = ["make", "model"];

const ROW_KEY: Record<VehicleField, keyof VehicleInfoSelectionInterface> = {
  make: "make",
  model: "model",
  year: "makeYear",
  variant: "variant",
  series: "series",
  engineCapacity: "engineCapacity",
  transmission: "transmission"
};

const EMPTY_VALUES = ["", "-", "--"];

// Variant / series / transmission can be blank in [VehicleInfo]; those rows are picked via a
// "-" option and sent to the API as "-". When the matching rows only have blanks, "-" is the
// sole option and gets auto-filled, so series / transmission become optional for that vehicle.
const BLANK_ALLOWED_FIELDS: VehicleField[] = ["variant", "series", "transmission"];
const BLANK_OPTION_VALUE = "-";
const BLANK_OPTION_LABEL = "-";
const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg", "application/pdf"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

@Component({
  selector: "app-missing-vehicle-info-dialog",
  templateUrl: "./missing-vehicle-info-dialog.component.html",
  styleUrls: ["./missing-vehicle-info-dialog.component.scss"]
})
export class MissingVehicleInfoDialogComponent implements OnInit, OnDestroy {
  readonly fields = VEHICLE_FIELDS;
  readonly labelKeys: Record<VehicleField, string> = {
    make: "missingVehicleInfo.make",
    model: "missingVehicleInfo.model",
    year: "missingVehicleInfo.year",
    variant: "missingVehicleInfo.variant",
    series: "missingVehicleInfo.series",
    engineCapacity: "missingVehicleInfo.engineCapacity",
    transmission: "missingVehicleInfo.transmission"
  };

  frmGroup: FormGroup;
  vehicleList: VehicleInfoSelectionInterface[] = [];
  options = {} as Record<VehicleField, VehicleOption[]>;
  filteredOptions = {} as Record<VehicleField, VehicleOption[]>;
  searchText = {} as Record<VehicleField, string>;

  lockedFields: VehicleField[] = [];

  isMobile = false;
  mobileView: "choice" | "form" = "form";

  grantFile: File | null = null;
  grantFileUrl: string | null = null;
  fileError: string | null = null;

  isLoading = false;
  isSubmitting = false;

  private destroy$ = new Subject<void>();

  constructor(
    private motorQuotationDataService: MotorQuotationDataService,
    private alertService: AlertService,
    private screenSizeService: ScreenSizeService,
    public dialogRef: MatDialogRef<MissingVehicleInfoDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      motorRequestId: string;
      request: MotorRequestInterface;
    }
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.screenSizeService.isMobile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((x) => (this.isMobile = x));
    // Mobile always starts on the "Know your vehicle details?" / upload choice.
    this.mobileView = "choice";
    this.getVehicleInfoSelectionList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.grantFileUrl) URL.revokeObjectURL(this.grantFileUrl);
  }

  private initForm() {
    const request = this.data.request;
    const retrieved: Record<VehicleField, string | number> = {
      make: request.make,
      model: request.model,
      year: request.year ? Number(request.year) : null,
      variant: request.variant,
      series: request.series,
      engineCapacity: request.engineCC,
      transmission: request.transmission
    };

    this.frmGroup = new FormGroup({});
    // Prefill only up to the first missing field: anything below it depends on the fields above
    // (e.g. CC / transmission vary by year, variant and series) and would change anyway.
    // It is auto-filled again later if only one option remains.
    let hasGapAbove = false;
    VEHICLE_FIELDS.forEach((field) => {
      const value =
        hasGapAbove || this.isEmpty(retrieved[field]) ? null : retrieved[field];
      const isLocked = value != null && LOCKABLE_FIELDS.includes(field);
      // Completeness is checked in isDetailsValid; Validators.required would reject the "-" option.
      this.frmGroup.addControl(
        field,
        new FormControl({ value, disabled: isLocked })
      );
      if (isLocked) this.lockedFields.push(field);
      if (value == null) hasGapAbove = true;
      this.options[field] = [];
      this.filteredOptions[field] = [];
      this.searchText[field] = "";
    });

    this.updateFieldAvailability();
  }

  private getVehicleInfoSelectionList() {
    this.isLoading = true;
    this.motorQuotationDataService.getVehicleInfoSelectionList().subscribe({
      next: (x) => {
        this.vehicleList = x.filter((row) => row.isActive);
        this.isLoading = false;
        this.refreshOptions();
        this.unlockModelIfNoVariant();
      },
      error: (err) => {
        this.isLoading = false;
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  onFieldChange(field: VehicleField) {
    const index = VEHICLE_FIELDS.indexOf(field);
    // A new parent selection invalidates everything below it (auto-filled again if only one option).
    VEHICLE_FIELDS.slice(index + 1).forEach((child) => {
      if (!this.isLocked(child)) this.frmGroup.controls[child].setValue(null);
    });
    this.refreshOptions(index + 1);
  }

  // Retrieved data stops at model, but that model has no variant in the selection list:
  // the customer could never complete the form, so let them change the model.
  private unlockModelIfNoVariant() {
    const isDataUpToModel =
      this.isLocked("model") && this.isEmpty(this.data.request.variant);
    if (!isDataUpToModel || this.options.variant.length) return;

    this.lockedFields = this.lockedFields.filter((x) => x != "model");
    // Keeps the retrieved model if it is in the list; otherwise clears it for the customer to pick.
    this.refreshOptions(VEHICLE_FIELDS.indexOf("model"));
  }

  // Rebuild options from `fromIndex` down the cascade, dropping values that are no longer valid.
  private refreshOptions(fromIndex: number = 0) {
    for (let i = fromIndex; i < VEHICLE_FIELDS.length; i++) {
      const field = VEHICLE_FIELDS[i];
      const control = this.frmGroup.controls[field];
      const rows = this.getRowsMatching(VEHICLE_FIELDS.slice(0, i));
      this.options[field] = this.buildOptions(field, rows);
      this.filterOptions(field);

      if (this.isLocked(field)) continue;

      const values = this.options[field].map((x) => x.value);
      if (control.value != null && !values.includes(control.value)) {
        // Retrieved values may differ in casing from the selection list.
        const match = values.find((x) => this.isSameValue(x, control.value));
        control.setValue(match ?? null);
      }
      if (
        control.value == null &&
        values.length == 1 &&
        !this.isBlocked(field)
      ) {
        control.setValue(values[0]);
      }
    }
    this.updateFieldAvailability();
  }

  // Fields are filled top to bottom: a field opens only once every field above it has a value.
  private updateFieldAvailability() {
    VEHICLE_FIELDS.forEach((field) => {
      if (this.isLocked(field)) return;
      const control = this.frmGroup.controls[field];
      if (this.isBlocked(field)) control.disable({ emitEvent: false });
      else control.enable({ emitEvent: false });
    });
  }

  private isBlocked(field: VehicleField) {
    const value = this.frmGroup.getRawValue();
    return VEHICLE_FIELDS.slice(0, VEHICLE_FIELDS.indexOf(field)).some(
      (x) => value[x] == null
    );
  }

  private isLocked(field: VehicleField) {
    return this.lockedFields.includes(field);
  }

  private getRowsMatching(fields: VehicleField[]) {
    const value = this.frmGroup.getRawValue();
    return this.vehicleList.filter((row) =>
      fields.every((field) => {
        const selected = value[field];
        if (selected == null) return true;
        if (BLANK_ALLOWED_FIELDS.includes(field)) {
          const isBlankRow = this.isBlankRow(row, field);
          if (selected === BLANK_OPTION_VALUE || isBlankRow) {
            return selected === BLANK_OPTION_VALUE && isBlankRow;
          }
        }
        return this.isSameValue(row[ROW_KEY[field]], selected);
      })
    );
  }

  private buildOptions(
    field: VehicleField,
    rows: VehicleInfoSelectionInterface[]
  ): VehicleOption[] {
    const map = new Map<string | number, string>();
    rows.forEach((row) => {
      if (BLANK_ALLOWED_FIELDS.includes(field) && this.isBlankRow(row, field)) {
        map.set(BLANK_OPTION_VALUE, BLANK_OPTION_LABEL);
        return;
      }
      const value = row[ROW_KEY[field]] as string | number;
      if (this.isEmpty(value)) return;
      const label =
        field == "engineCapacity" ? `${value} ${row.engineUOM}` : `${value}`;
      map.set(value, label);
    });

    const options = Array.from(map, ([value, label]) => ({ value, label }));
    if (map.has(BLANK_OPTION_VALUE)) {
      // Keep "-" at the top of the list.
      const rest = options.filter((x) => x.value !== BLANK_OPTION_VALUE);
      return [
        { value: BLANK_OPTION_VALUE, label: BLANK_OPTION_LABEL },
        ...this.sortOptions(field, rest)
      ];
    }
    return this.sortOptions(field, options);
  }

  private sortOptions(field: VehicleField, options: VehicleOption[]) {
    return field == "year"
      ? options.sort((a, b) => Number(b.value) - Number(a.value))
      : field == "engineCapacity"
      ? options.sort((a, b) => Number(a.value) - Number(b.value))
      : options.sort((a, b) => a.label.localeCompare(b.label));
  }

  filterOptions(field: VehicleField) {
    const search = (this.searchText[field] || "").toLowerCase();
    this.filteredOptions[field] = this.options[field].filter((x) =>
      x.label.toLowerCase().includes(search)
    );
  }

  isFieldVisible(field: VehicleField) {
    return !(this.isOptionalWhenEmpty(field) && !this.options[field].length);
  }

  private isOptionalWhenEmpty(field: VehicleField) {
    return field == "series" || field == "transmission";
  }

  // -- Upload

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      this.fileError = "missingVehicleInfo.invalidFileType";
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.fileError = "missingVehicleInfo.invalidFileSize";
      return;
    }

    this.removeFile();
    this.grantFile = file;
    this.grantFileUrl = URL.createObjectURL(file);
  }

  openFile() {
    if (this.grantFileUrl) window.open(this.grantFileUrl, "_blank");
  }

  removeFile() {
    if (this.grantFileUrl) URL.revokeObjectURL(this.grantFileUrl);
    this.grantFile = null;
    this.grantFileUrl = null;
    this.fileError = null;
  }

  // -- Submit

  // Uses raw values so locked / waiting (disabled) fields are still checked; "-" counts as filled.
  get isDetailsValid() {
    const value = this.frmGroup.getRawValue();
    return VEHICLE_FIELDS.every((field) =>
      this.isOptionalWhenEmpty(field) && !this.options[field].length
        ? true
        : value[field] != null
    );
  }

  get canSubmit() {
    if (this.isSubmitting || this.isLoading) return false;
    if (this.isMobile && this.mobileView == "choice") return !!this.grantFile;
    return this.isDetailsValid || !!this.grantFile;
  }

  submit() {
    if (!this.canSubmit) return;

    const formData = new FormData();
    formData.append("MotorRequestId", this.data.motorRequestId);
    if (this.grantFile) formData.append("VehicleGrant", this.grantFile);

    // Details win when complete (with or without a grant); otherwise send the grant alone.
    const sendDetails = this.isDetailsValid;
    if (sendDetails) {
      const value = this.frmGroup.getRawValue();
      formData.append("Make", value.make);
      formData.append("VehicleModel", value.model);
      formData.append("Year", `${value.year}`);
      formData.append("EngineCC", `${value.engineCapacity}`);
      formData.append("Variant", value.variant ?? BLANK_OPTION_VALUE);
      // Series / transmission are hidden (null) when the vehicle has none; send them as blank.
      formData.append("Transmission", value.transmission ?? BLANK_OPTION_VALUE);
      formData.append("Series", value.series ?? BLANK_OPTION_VALUE);
    }

    const request$ = sendDetails
      ? this.motorQuotationDataService.processVehicleInfo(formData)
      : this.motorQuotationDataService.processVehicleGrant(formData);

    this.isSubmitting = true;
    request$.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  private isBlankRow(row: VehicleInfoSelectionInterface, field: VehicleField) {
    const value = `${row[ROW_KEY[field]] ?? ""}`.trim();
    return EMPTY_VALUES.includes(value);
  }

  private isSameValue(a: unknown, b: unknown) {
    return `${a}`.trim().toUpperCase() == `${b}`.trim().toUpperCase();
  }

  private isEmpty(value: unknown) {
    return value == null || `${value}`.trim() == "";
  }
}
